import React, { useEffect } from 'react';
import { LandingHero } from './landing/LandingHero';
import { LandingFeatures } from './landing/LandingFeatures';
import { LandingPricing } from './landing/LandingPricing';
import { LandingFaq } from './landing/LandingFaq';
import { LandingFooter } from './landing/LandingFooter';

interface LandingPageProps {
  onSignInClick: () => void;
  onSelectPlan?: (tierId: string) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onSignInClick, onSelectPlan }) => {
  // Premium Brand Colours
  const brandPrimary = '#7C3AED'; // Vibrant Violet
  const brandSecondary = '#F59E0B'; // Warm Yellow/Amber
  const brandDark = '#111827';
  const brandLight = '#FAFAFA';
  const brandCard = '#FFFFFF';

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const fadeInUp: any = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  const staggerContainer: any = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const scaleUp: any = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        background: brandLight,
        color: brandDark,
        overflowX: 'hidden',
        fontFamily: '"Plus Jakarta Sans", sans-serif',
      }}
    >
      {/* Decorative Background Elements */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, rgba(250,250,250,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '20%',
          right: '-10%',
          width: '40vw',
          height: '40vw',
          background: 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, rgba(250,250,250,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Navbar */}
      <nav
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 5%',
          position: 'fixed',
          width: '100%',
          top: 0,
          background: 'rgba(250, 250, 250, 0.8)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${brandPrimary}, #4F46E5)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 900,
              fontSize: '1.2rem',
              boxShadow: '0 4px 10px rgba(124,58,237,0.3)',
            }}
          >
            F
          </div>
          <h1 style={{ fontSize: '1.4rem', margin: 0, fontWeight: 800, letterSpacing: '-0.03em', color: brandDark }}>
            FileSharez
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center' }}>
          <button
            onClick={onSignInClick}
            style={{ background: 'transparent', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '1rem', color: brandDark }}
          >
            Log in
          </button>
          <a
            href="#pricing"
            style={{
              background: brandDark,
              color: 'white',
              padding: '0.7rem 1.4rem',
              borderRadius: '30px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              transition: 'transform 0.2s',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            Get started
          </a>
        </div>
      </nav>

      <main style={{ position: 'relative', zIndex: 10, paddingTop: '8rem' }}>
        <LandingHero
          onSignInClick={onSignInClick}
          brandPrimary={brandPrimary}
          brandSecondary={brandSecondary}
          brandDark={brandDark}
          fadeInUp={fadeInUp}
          staggerContainer={staggerContainer}
          scaleUp={scaleUp}
        />

        <LandingFeatures fadeInUp={fadeInUp} staggerContainer={staggerContainer} />

        <LandingPricing
          onSelectPlan={onSelectPlan}
          brandPrimary={brandPrimary}
          brandSecondary={brandSecondary}
          brandDark={brandDark}
          fadeInUp={fadeInUp}
        />

        <LandingFaq
          brandPrimary={brandPrimary}
          brandCard={brandCard}
          fadeInUp={fadeInUp}
          staggerContainer={staggerContainer}
        />

        <LandingFooter
          onSignInClick={onSignInClick}
          brandPrimary={brandPrimary}
          brandDark={brandDark}
        />
      </main>
    </div>
  );
};
